<template>
  <header
    :class="[
      'absolute w-full z-[1001] text-white grid grid-rows-[auto_1fr] bg-gradient-to-b from-dark/75',
      '',
      isMobileNavOpen ? 'h-dvh max-h-dvh' : '',
    ]"
    ref="menu"
  >
    <div
      :class="[
        'flex flex-col w-full bg-dark items-center',
        'lg:flex-row lg:items-end lg:justify-end lg:gap-6',
      ]"
    >
      <div class="container py-2 hidden lg:flex justify-between">
        <span>{{ festivalDateInfo }}</span>

        <div class="flex items-center space-x-4 text-xl leading-none">
          <Icon v-if="isAuthPending" class="-mt-2 text-primary text-sm" name="Spinner" />

          <template v-else-if="isSignedIn">
            <NuxtLink :to="auth?.logOutUrl.value">
              {{ translate('label.logOut') }}
            </NuxtLink>

            <NuxtLink :to="myProfilePath">
              {{ translate('label.yourProfile') }}
            </NuxtLink>
          </template>

          <NuxtLink v-else :to="auth?.logInUrl.value">
            {{ translate('label.logIn') }}
          </NuxtLink>

          <LanguageSelector class="lt-lg:hidden" />

          <NuxtLink
            class="bg-light bg-opacity-15 rounded-full px-3 pb-1 lowercase cursor-pointer hover:bg-opacity-25"
            @click="focusSearch"
          >
            {{ translate('label.search') }}
          </NuxtLink>
        </div>
      </div>
    </div>
    <nav
      :class="[
        'grid grid-rows-[auto_1fr_auto] overflow-y-auto',
        { 'bg-light text-dark': isMobileNavOpen },
      ]"
    >
      <div
        :class="[
          'container overflow-hidden relative flex items-start justify-between',
          'lt-lg:pt-5 pb-2',
          'lg:overflow-visible lg:grid-cols-[1fr_170px_1fr] lg:static lg:pt-7 lg:pb-6',
        ]"
      >
        <NuxtLink :to="homepageLink" class="w-32 lg:w-80">
          <NuxtImg
            v-if="logoSource"
            provider="cloudinary"
            :src="encodeURI(logoSource)"
            role="navigation"
            aria-label="Home"
            sizes="sm:100px lg:326px"
            quality="100"
            format="svg"
            :class="['w-full', { 'brightness-0': isMobileNavOpen }]"
          />
        </NuxtLink>

        <div :class="['flex justify-end items-center gap-7']">
          <NuxtLink class="hidden lg:inline-flex flex-col items-center space-y-2" :to="programLink">
            <Icon class="text-2xl" name="MenuProgram" />
            <span class="uppercase">{{ translate('mainMenu.program') }}</span>
          </NuxtLink>
          <NuxtLink
            class="hidden lg:inline-flex flex-col items-center space-y-2"
            :to="supportUsLink"
          >
            <Icon class="text-2xl" name="MenuSupport" />
            <span class="uppercase">{{ translate('mainMenu.supportUs') }}</span>
          </NuxtLink>
          <button
            @click="handleMobileNavToggle"
            :class="['inline-flex flex-col items-center lg:space-y-2']"
          >
            <Icon
              :class="['text-xl lg:text-2xl transition-all', { 'rotate-45': isMobileNavOpen }]"
              name="Plus"
            />
            <span class="uppercase">{{ translate('mainMenu.menu') }}</span>
          </button>
        </div>
      </div>
      <div
        :class="[
          'relative container w-full flex-col items-center overflow-y-auto overflow-x-hidden',
          isMobileNavOpen ? 'flex' : 'hidden',
        ]"
      >
        <div class="lg:hidden grid gap-3 grid-cols-2 w-full mb-5">
          <div class="border border-dark p-4 text-center leading-none text-xl col-span-2">
            {{ festivalDateInfo }}
          </div>
          <NuxtLink
            class="inline-flex flex-col items-center gap-2 p-3 bg-light-accent"
            :to="programLink"
          >
            <Icon class="text-2xl" name="MenuProgram" />
            <span class="uppercase">{{ translate('mainMenu.program') }}</span>
          </NuxtLink>
          <NuxtLink
            class="inline-flex flex-col items-center gap-2 p-3 bg-light-accent"
            :to="supportUsLink"
          >
            <Icon class="text-2xl" name="MenuSupport" />
            <span class="uppercase">{{ translate('mainMenu.supportUs') }}</span>
          </NuxtLink>

          <span class="text-center col-span-2" v-if="isAuthPending">
            <Icon class="text-primary text-lg" name="Spinner" />
          </span>
          <div class="flex gap-2 col-span-2" v-else-if="isSignedIn">
            <MxButton
              class="rounded-none w-full text-xl"
              size="large"
              :href="myProfilePath"
              @click="handleMobileNavClose"
            >
              {{ translate('label.yourProfile') }}
            </MxButton>
            <MxButton class="rounded-none w-full text-xl" size="large" :href="auth.logOutUrl.value">
              {{ translate('label.logOut') }}
            </MxButton>
          </div>
          <div class="col-span-2" v-else>
            <MxButton class="rounded-none w-full text-xl" size="large" :href="auth.logInUrl.value">
              {{ translate('label.logIn') }}
            </MxButton>
          </div>
        </div>
        <div class="lg:grid lg:grid-cols-12 gap-6 w-full">
          <div class="lg:col-start-2 lg:col-span-6">
            <div class="lg:grid lg:grid-cols-2 gap-6 grid-flow-row">
              <MenuSection v-for="(section, idx) in story.mainMenu" :section="section" :key="idx">
                <MenuLink
                  v-for="(item, itemIdx) in section.children"
                  :key="`item-${itemIdx}`"
                  :to="item.link"
                  >{{ item.label }}</MenuLink
                >
              </MenuSection>
            </div>
          </div>

          <div class="hidden lg:block lg:col-span-4">
            <MenuHeading class="mb-3">{{ translate('heading.news') }}</MenuHeading>
            <div class="space-y-10">
              <MenuNews v-for="news in story.news" :story="news" />
            </div>
          </div>
        </div>
      </div>
      <div
        :class="[
          'container lg:grid-cols-12 py-3 lg:py-12 gap-6',
          isMobileNavOpen ? 'lg:grid' : 'hidden',
        ]"
      >
        <div class="lg:col-start-2 lg:col-span-6"><Search hideLabel ref="search" /></div>
        <div class="flex items-center justify-center p-1 mt-3 border-t border-grey pb-0 lg:hidden">
          <LanguageSelector class="lg:hidden" />
        </div>
        <div class="hidden lg:block col-span-4">
          <span v-if="isAuthPending">
            <Icon class="text-primary text-lg" name="Spinner" />
          </span>
          <div v-else-if="isSignedIn">
            <MxButton size="large" :href="auth.logOutUrl.value">
              {{ translate('label.logOut') }}
              <Icon class="text-xl" name="ArrowRight" width="1.5" />
            </MxButton>
          </div>
          <div v-else>
            <MxButton size="large" :href="auth.logInUrl.value">
              {{ translate('label.logIn') }}
              <Icon class="text-xl" name="ArrowRight" width="1.5" />
            </MxButton>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { provide, ref, computed } from 'vue';
import LanguageSelector from '~/components/LanguageSelector/LanguageSelector.vue';

import { useAuth } from '~/composables/useAuth';
import { useTranslation } from '~/composables/useTranslation';

import MenuHeading from '~/components/Header/MenuHeading.vue';
import MenuLink from '~/components/Header/MenuLink.vue';
import MenuSection from '~/components/Header/MenuSection.vue';
import MenuNews from '~/components/Header/MenuNews.vue';

const translate = useTranslation();
const runtimeConfig = useRuntimeConfig();

const language = useState('language');
const homepageLink = computed(() =>
  language.value === 'no'
    ? `${runtimeConfig.public.siteUrl}/`
    : `${runtimeConfig.public.siteUrl}/en`,
);
const programLink = computed(() =>
  language.value === 'no'
    ? `${runtimeConfig.public.siteUrl}/program`
    : `${runtimeConfig.public.siteUrl}/en/programme`,
);
const supportUsLink = computed(() =>
  language.value === 'no'
    ? `${runtimeConfig.public.siteUrl}/stottespillere/stott-oss`
    : `${runtimeConfig.public.siteUrl}/en/supporters/support-us`,
);

const auth = useAuth();
const authUserIdComputed = computed(() => auth?.user?.value?.id);
const isAuthPending = computed(() => auth?.user?.value?.id === null);
const isSignedIn = ref<boolean | null>(null);

watch(
  [authUserIdComputed],
  async ([userId]) => {
    isSignedIn.value = !!userId;
  },
  {
    immediate: true,
  },
);

const menu = ref();
const search = ref();

const submenu = ref(null);
const setSubmenu = (newSubmenu: any) => {
  submenu.value = newSubmenu;
};
provide('submenu', { submenu, setSubmenu });

const isMobileNavOpen = useState('isMobileNavOpen', () => false);

useHead({
  bodyAttrs: {
    class: () => [isMobileNavOpen.value ? 'h-screen overflow-hidden relative' : ''].join(' '),
  },
});

const handleMobileNavOpen = () => {
  isMobileNavOpen.value = true;
  //activate();
};

const handleMobileNavClose = () => {
  isMobileNavOpen.value = false;
  //deactivate();
};

const handleMobileNavToggle = () => {
  if (!isMobileNavOpen.value) {
    handleMobileNavOpen();
  } else {
    handleMobileNavClose();
  }
};

const focusSearch = async () => {
  handleMobileNavOpen();
  await nextTick();
  search.value.focusInput();
};

const storyMultiLang = useState<any>('headerContent');
const story = computed(() => storyMultiLang.value[String(language.value)]);

const { logo, festivalDateInfo } = story.value || {};

const logoSource = computed(() => logo?.public_id);

const myProfilePath = computed(() => {
  const path = language.value === 'no' ? '/min-profil' : '/en/my-profile';

  return `${runtimeConfig.public.siteUrl}${path}`;
});

// i know, right?

onMounted(() => {});

onUnmounted(() => {});
</script>

<style scoped></style>
